/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * 
 * OTIO Converter
 * Transforms internal TimelineState into OTIOTimeline structure
 * compliant with OpenTimelineIO schema for export.
 */

import { TimelineState, DEFAULT_FPS } from '../types/timeline';
import { OTIOTimeline, OTIOTrack, createRationalTime } from '../types/otio';
import { buildTrackItems } from './otioUtils';

export function timelineStateToOTIO(state: TimelineState): OTIOTimeline {
    const fps = state.project?.fps || DEFAULT_FPS;

    // Group segments by track
    const segmentsByTrackId: Record<string, typeof state.segments> = {};
    for (const track of state.tracks) {
        segmentsByTrackId[track.id] = state.segments.filter(s => s.trackId === track.id);
    }

    // Build Tracks
    const otioTracks: OTIOTrack[] = state.tracks.map(track => {
        const trackSegments = segmentsByTrackId[track.id] || [];
        const children = buildTrackItems(trackSegments, fps);

        return {
            id: track.id,
            name: track.name,
            kind: track.type,
            children: children,
            muted: track.muted,
            locked: track.locked,
            visible: track.visible
        };
    });

    return {
        id: state.project?.id || 'timeline-export',
        name: state.project?.title || 'Timeline Export',
        fps: fps as any, // Cast to literal type if needed
        globalStartTime: createRationalTime(0, fps),
        tracks: otioTracks,
        metadata: {
            aspect: state.project?.aspect,
            createdAt: new Date().toISOString()
        }
    };
}
